/**
 * A React wrapper around qTip for displaying tooltips on hover.
 *
 * This must wrap a single element. If you need to wrap more, create the
 * wrapper element yourself.
 */

import * as React from "react";
import {findDOMNode} from "react-dom";

import $ from "#jquery/jquery-with-qtip.js";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";

type Props = {
    // There must be children to hover over!
    children: React.ReactNode;
    // The contents of the tooltip to display. Note that the contents of
    // this cannot be interactive, as this is converted to static markup
    // before being rendered in the tooltip.
    tipContents: React.ReactNode;
    // The position of the tooltip.
    // "my" dictates which side/corner of the tooltip has the arrow. "at"
    // dictates where where the bottom points to. See
    // http://qtip2.com/options#position for more details.
    position?: {
        my: string;
        at: string;
    };
};

class WithHoverTip extends React.Component<Props> {
    _unmountContents: (() => void) | undefined;

    static defaultProps: {
        position: {
            at: string;
            my: string;
        };
    } = {
        position: {
            my: "bottom center",
            at: "top center",
        },
    };

    componentDidMount() {
        this.componentDidUpdate();
    }

    componentDidUpdate() {
        const node = findDOMNode(this);
        if (!node) {
            return;
        }
        ($(node) as any).qtip({
            content: this.makeQTipContents(),
            style: "qtip-light",
            position: this.props.position,
            hide: {
                fixed: true,
                delay: 150,
            },
        });
    }

    componentWillUnmount() {
        const node = findDOMNode(this);
        if (!node) {
            return;
        }
        ($(node) as any).qtip("destroy");

        this.unmountQTip();
    }

    unmountQTip: () => void = () => {
        const unmount = this._unmountContents;
        this._unmountContents = undefined;
        unmount?.();
    };

    makeQTipContents: () => any = () => {
        this.unmountQTip();

        const mountElement = document.createElement("span");
        this._unmountContents = deprecatedReactRender(
            <span children={this.props.tipContents} />,
            mountElement,
        );

        return $(mountElement);
    };

    render(): any {
        let child = this.props.children;

        // We use an Array.isArray check instead of React.Children.only here
        // because React.Children.only doesn't play nicely with strings or with
        // React fragments.
        if (Array.isArray(child)) {
            if (child.length !== 1) {
                throw Error("QuestionTip should have a single child element.");
            }
            child = child[0];
        }

        return child;
    }
}

export default WithHoverTip;
